import { AxiosError } from "axios";
import { Controller, useForm } from "react-hook-form";

import { AgentVersionName } from "src/agents/AgentVersionName";
import {
  VERSION_NAME_MAX_LENGTH,
  VERSION_NAME_MIN_LENGTH,
} from "src/agents/constants";
import { useAgentVersions, useCreateAgentVersion } from "src/agents/queries";
import { AgentVersion } from "src/agents/types";
import { Button } from "src/base-components/Button";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { Select } from "src/base-components/Select";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { logger } from "src/utils/logger";

type CreateAgentVersionModalProps = {
  open: boolean;
  onClose: () => void;
  agentId: string;
  versionId?: string;
  startFromScratch?: boolean;
  onCreateSuccess?: (agentId: string, newVersion: AgentVersion) => void;
};

type CreateAgentVersionForm = {
  agentId: string;
  versionId: string;
  versionName: string;
};

const START_FROM_SCRATCH_KEY = "scratch";

export const CreateAgentVersionModal: React.FC<
  CreateAgentVersionModalProps
> = ({
  open,
  onClose: onCloseProp,
  agentId,
  versionId,
  startFromScratch = false,
  onCreateSuccess,
}) => {
  const { data: versions, isLoading } = useAgentVersions(agentId);
  const { mutateAsync: createVersion } = useCreateAgentVersion();
  const form = useForm<CreateAgentVersionForm>({
    mode: "onChange",
    values: {
      agentId,
      versionId: startFromScratch
        ? START_FROM_SCRATCH_KEY
        : (versionId ?? versions?.[0]?.id ?? START_FROM_SCRATCH_KEY),
      versionName: "",
    },
  });

  const onClose = () => {
    form.reset();
    onCloseProp();
  };

  const onSubmit = async (data: CreateAgentVersionForm) => {
    try {
      const isFromScratch = data.versionId === START_FROM_SCRATCH_KEY;

      const newVersion = await createVersion({
        agentId: data.agentId,
        versionName: data.versionName,
        sourceVersionId: isFromScratch ? undefined : data.versionId,
      });

      toastActions.success({
        title: "Version created",
        description: `Successfully created ${data.versionName}`,
      });

      if (onCreateSuccess) {
        onCreateSuccess(data.agentId, newVersion);
      }

      onClose();
    } catch (error) {
      logger.error("Failed to create version", error);
      const errorMessage =
        error instanceof AxiosError && error.status === 409
          ? "Version name already exists"
          : "An unknown error occurred";
      toastActions.failure({
        title: "Failed to create version",
        description: errorMessage,
      });
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header>Create version</Modal.Header>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Modal.Content>
          <div className="flex flex-col gap-5">
            <div className="flex flex-col">
              <Label mb="mb-0.5" required>
                Create from
              </Label>
              <span className="mb-2 text-gray-500 font-inter-normal-12px">
                Select a version to duplicate
              </span>
              <Controller
                control={form.control}
                name="versionId"
                render={({ field, fieldState }) => {
                  const versionOptions =
                    versions?.map((version) => ({
                      key: version.id,
                      value: (
                        <AgentVersionName
                          name={version.name}
                          status={version.status}
                        />
                      ),
                    })) ?? [];

                  const allOptions = [
                    {
                      key: START_FROM_SCRATCH_KEY,
                      value: <span>Start from scratch</span>,
                    },
                    ...versionOptions,
                  ];

                  return (
                    <>
                      <Select
                        dataLoc="create-agent-version-select"
                        errored={!!fieldState.error}
                        options={allOptions}
                        value={field.value}
                        onChange={field.onChange}
                      />
                      {fieldState.error?.message && (
                        <ErrorHint>{fieldState.error.message}</ErrorHint>
                      )}
                    </>
                  );
                }}
                rules={{
                  required: "Please select a version to create from",
                }}
              />
            </div>
            <div>
              <Label required>Version name</Label>
              <Input
                errored={!!form.formState.errors.versionName}
                fullWidth
                {...form.register("versionName", {
                  required: "Version name is required",
                  minLength: {
                    value: VERSION_NAME_MIN_LENGTH,
                    message: `Version name must be at least ${VERSION_NAME_MIN_LENGTH} character`,
                  },
                  maxLength: {
                    value: VERSION_NAME_MAX_LENGTH,
                    message: `Version name must be less than ${VERSION_NAME_MAX_LENGTH} characters`,
                  },
                })}
                placeholder="Type version name"
              />
              {form.formState.errors.versionName && (
                <ErrorHint>
                  {form.formState.errors.versionName?.message}
                </ErrorHint>
              )}
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              disabled={
                isLoading ||
                form.formState.isSubmitting ||
                !form.formState.isValid
              }
              htmlType="submit"
              loading={isLoading || form.formState.isSubmitting}
              variant="primary"
            >
              Create
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
